import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import TextField from "@components/ui/textfields/TextField";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useRef, useState, useCallback } from "react";
import { showNotification } from "context/notification.service";
import { projectTemplatesApi } from "@services/api";
import { ProjectFolder, ProjectSubFolder, ProjectTheme, Template } from "types";
import { colors } from "sexy";


type TabKey = "steps" | "folders";

export const ProjectTemplateModal = ({
  onClose,
  templateId,
}: {
  onClose: () => void;
  templateId: string;
}) => {
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const updateProjectTemplate = useProjectStore(
    (state) => state.updateProjectTemplate,
  );
  const emailTemplates = useProjectStore((state) => state.emailTemplates);
  const userData = useDataStore((state) => state.userData);

  const existingTemplate = projectTemplates.find((t) => t.id === templateId);
  const isNew = !existingTemplate;

  const [name, setName] = useState(
    existingTemplate?.infos?.general?.name || "",
  );
  const [themes, setThemes] = useState<ProjectTheme[]>(
    existingTemplate ? structuredClone(existingTemplate.themes) : [],
  );
  const [folders, setFolders] = useState<ProjectFolder[]>(
    existingTemplate ? structuredClone(existingTemplate.folders || []) : [],
  );
  const [activeTab, setActiveTab] = useState<TabKey>("steps");
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set());
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [themeToDelete, setThemeToDelete] = useState<string | null>(null);

  // Drag and drop for themes
  const [draggedThemeIdx, setDraggedThemeIdx] = useState<number | null>(null);
  const [dragOverThemeIdx, setDragOverThemeIdx] = useState<number | null>(null);
  const themeDragging = useRef(false);

  const handleThemePointerDown = useCallback((e: React.PointerEvent, index: number) => {
    e.stopPropagation();
    themeDragging.current = true;
    setDraggedThemeIdx(index);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleThemePointerMove = useCallback((e: React.PointerEvent) => {
    if (!themeDragging.current) return;
    const container = (e.currentTarget as HTMLElement).closest("[data-themes-container]");
    if (!container) return;
    const els = Array.from(container.querySelectorAll("[data-theme-index]"));
    for (const el of els) {
      const rect = el.getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        setDragOverThemeIdx(parseInt(el.getAttribute("data-theme-index")!, 10));
        break;
      }
    }
  }, []);

  const handleThemePointerUp = useCallback(() => {
    if (themeDragging.current && draggedThemeIdx !== null && dragOverThemeIdx !== null && draggedThemeIdx !== dragOverThemeIdx) {
      setThemes((prev) => {
        const next = [...prev];
        const [moved] = next.splice(draggedThemeIdx, 1);
        next.splice(dragOverThemeIdx, 0, moved);
        return next;
      });
    }
    themeDragging.current = false;
    setDraggedThemeIdx(null);
    setDragOverThemeIdx(null);
  }, [draggedThemeIdx, dragOverThemeIdx]);

  // Drag and drop for steps (within a theme)
  const [dragStepCtx, setDragStepCtx] = useState<{ themeId: string; idx: number } | null>(null);
  const [dragOverStepIdx, setDragOverStepIdx] = useState<number | null>(null);
  const stepDragging = useRef(false);

  const handleStepPointerDown = useCallback((e: React.PointerEvent, themeId: string, index: number) => {
    e.stopPropagation();
    stepDragging.current = true;
    setDragStepCtx({ themeId, idx: index });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleStepPointerMove = useCallback((e: React.PointerEvent) => {
    if (!stepDragging.current) return;
    const container = (e.currentTarget as HTMLElement).closest("[data-steps-container]");
    if (!container) return;
    const els = Array.from(container.querySelectorAll("[data-step-index]"));
    for (const el of els) {
      const rect = el.getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        setDragOverStepIdx(parseInt(el.getAttribute("data-step-index")!, 10));
        break;
      }
    }
  }, []);

  const handleStepPointerUp = useCallback(() => {
    if (stepDragging.current && dragStepCtx !== null && dragOverStepIdx !== null && dragStepCtx.idx !== dragOverStepIdx) {
      const { themeId, idx: fromIdx } = dragStepCtx;
      const toIdx = dragOverStepIdx;
      setThemes((prev) =>
        prev.map((t) => {
          if (t.id !== themeId) return t;
          const steps = [...t.steps];
          const [moved] = steps.splice(fromIdx, 1);
          steps.splice(toIdx, 0, moved);
          return { ...t, steps };
        }),
      );
    }
    stepDragging.current = false;
    setDragStepCtx(null);
    setDragOverStepIdx(null);
  }, [dragStepCtx, dragOverStepIdx]);

  // Drag and drop for control points (within a step)
  const [dragCpCtx, setDragCpCtx] = useState<{ themeId: string; stepId: string; idx: number } | null>(null);
  const [dragOverCpIdx, setDragOverCpIdx] = useState<number | null>(null);
  const cpDragging = useRef(false);

  const handleCpPointerDown = useCallback((e: React.PointerEvent, themeId: string, stepId: string, index: number) => {
    e.stopPropagation();
    cpDragging.current = true;
    setDragCpCtx({ themeId, stepId, idx: index });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleCpPointerMove = useCallback((e: React.PointerEvent) => {
    if (!cpDragging.current) return;
    const container = (e.currentTarget as HTMLElement).closest("[data-cps-container]");
    if (!container) return;
    const els = Array.from(container.querySelectorAll("[data-cp-index]"));
    for (const el of els) {
      const rect = el.getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        setDragOverCpIdx(parseInt(el.getAttribute("data-cp-index")!, 10));
        break;
      }
    }
  }, []);

  const handleCpPointerUp = useCallback(() => {
    if (cpDragging.current && dragCpCtx !== null && dragOverCpIdx !== null && dragCpCtx.idx !== dragOverCpIdx) {
      const { themeId, stepId, idx: fromIdx } = dragCpCtx;
      const toIdx = dragOverCpIdx;
      setThemes((prev) =>
        prev.map((t) => {
          if (t.id !== themeId) return t;
          return {
            ...t,
            steps: t.steps.map((s) => {
              if (s.id !== stepId) return s;
              const cps = [...s.controlpoints];
              const [moved] = cps.splice(fromIdx, 1);
              cps.splice(toIdx, 0, moved);
              return { ...s, controlpoints: cps };
            }),
          };
        }),
      );
    }
    cpDragging.current = false;
    setDragCpCtx(null);
    setDragOverCpIdx(null);
  }, [dragCpCtx, dragOverCpIdx]);

  const toggleTheme = (themeId: string) => {
    setExpandedThemes((prev) => {
      const next = new Set(prev);
      if (next.has(themeId)) next.delete(themeId);
      else next.add(themeId);
      return next;
    });
  };

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const updateThemeLabel = (themeId: string, label: string) => {
    setThemes((prev) =>
      prev.map((t) => (t.id === themeId ? { ...t, label } : t)),
    );
  };

  const addTheme = () => {
    const id = `theme_${Date.now()}`;
    setThemes((prev) => [...prev, { id, label: "Nouveau thème", steps: [] }]);
    setExpandedThemes((prev) => new Set(prev).add(id));
  };

  const removeTheme = (themeId: string) => {
    setThemes((prev) => prev.filter((t) => t.id !== themeId));
  };

  const updateStepLabel = (themeId: string, stepId: string, label: string) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId ? { ...s, label } : s,
              ),
            }
          : t,
      ),
    );
  };

  const addStep = (themeId: string) => {
    const id = `step_${Date.now()}`;
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: [
                ...t.steps,
                { id, label: "Nouvelle étape", controlpoints: [] },
              ],
            }
          : t,
      ),
    );
    setExpandedSteps((prev) => new Set(prev).add(id));
  };

  const removeStep = (themeId: string, stepId: string) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? { ...t, steps: t.steps.filter((s) => s.id !== stepId) }
          : t,
      ),
    );
  };

  const updateControlPointLabel = (
    themeId: string,
    stepId: string,
    cpId: string,
    label: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.map((cp) =>
                        cp.id === cpId ? { ...cp, label } : cp,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const toggleControlPointClientVisibility = (
    themeId: string,
    stepId: string,
    cpId: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.map((cp) =>
                        cp.id === cpId ? { ...cp, hidden: !cp.hidden } : cp,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const updateControlPointEmailTemplate = (
    themeId: string,
    stepId: string,
    cpId: string,
    emailTemplateId: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.map((cp) =>
                        cp.id === cpId
                          ? {
                              ...cp,
                              associated_email_template_id:
                                emailTemplateId || undefined,
                            }
                          : cp,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const addControlPoint = (themeId: string, stepId: string) => {
    const id = `cp_${Date.now()}`;
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: [
                        ...s.controlpoints,
                        {
                          id,
                          label: "",
                          state: "default" as const,
                        },
                      ],
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };


  const removeControlPoint = (
    themeId: string,
    stepId: string,
    cpId: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.filter(
                        (cp) => cp.id !== cpId,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  // --- Folder helpers ---
  const addFolder = () => {
    const id = `folder_${Date.now()}`;
    setFolders((prev) => [...prev, { id, name: "Nouveau dossier", sub_folders: [] }]);
    setExpandedFolders((prev) => new Set(prev).add(id));
  };

  const removeFolder = (folderId: string) => {
    setFolders((prev) => prev.filter((f) => f.id !== folderId));
  };

  const updateFolderName = (folderId: string, name: string) => {
    setFolders((prev) =>
      prev.map((f) => (f.id === folderId ? { ...f, name } : f)),
    );
  };

  const toggleFolderNotify = (folderId: string) => {
    setFolders((prev) =>
      prev.map((f) =>
        f.id === folderId
          ? { ...f, notify_conductor_on_upload: !f.notify_conductor_on_upload }
          : f,
      ),
    );
  };

  const toggleSubFolderNotify = (folderId: string, subFolderId: string) => {
    setFolders((prev) =>
      prev.map((f) =>
        f.id === folderId
          ? {
              ...f,
              sub_folders: (f.sub_folders || []).map((sf) =>
                sf.id === subFolderId
                  ? {
                      ...sf,
                      notify_conductor_on_upload: !sf.notify_conductor_on_upload,
                    }
                  : sf,
              ),
            }
          : f,
      ),
    );
  };

  const addSubFolder = (folderId: string) => {
    const id = `subfolder_${Date.now()}`;
    setFolders((prev) =>
      prev.map((f) =>
        f.id === folderId
          ? {
              ...f,
              sub_folders: [
                ...(f.sub_folders || []),
                { id, name: "Nouveau sous-dossier" },
              ],
            }
          : f,
      ),
    );
  };

  const removeSubFolder = (folderId: string, subFolderId: string) => {
    setFolders((prev) =>
      prev.map((f) =>
        f.id === folderId
          ? {
              ...f,
              sub_folders: (f.sub_folders || []).filter(
                (sf) => sf.id !== subFolderId,
              ),
            }
          : f,
      ),
    );
  };

  const updateSubFolderName = (
    folderId: string,
    subFolderId: string,
    name: string,
  ) => {
    setFolders((prev) =>
      prev.map((f) =>
        f.id === folderId
          ? {
              ...f,
              sub_folders: (f.sub_folders || []).map((sf) =>
                sf.id === subFolderId ? { ...sf, name } : sf,
              ),
            }
          : f,
      ),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification({
        type: "error",
        message: "Le nom du modèle est obligatoire",
      });
      return;
    }

    if (isNew) {
      try {
        const result = await projectTemplatesApi.create({
          name,
          org_id: userData?.org_id || "",
          status: "active",
          themes,
          folders,
        });
        useProjectStore.getState().addProjectTemplate(result);
        showNotification({
          type: "success",
          message: "Modèle créé avec succès",
        });
        onClose();
      } catch (error) {
        console.error("Error creating project template:", error);
        showNotification({
          type: "error",
          message: "Erreur lors de la création du modèle",
        });
      }
    } else {
      const updated: Template = {
        ...existingTemplate,
        infos: {
          ...existingTemplate.infos,
          general: {
            ...existingTemplate.infos.general,
            name,
          },
        },
        themes,
        folders,
      };
      updateProjectTemplate(updated);
      showNotification({
        type: "success",
        message: "Modèle enregistré",
      });
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!existingTemplate) return;
    try {
      useProjectStore.getState().removeProjectTemplates([existingTemplate.id]);
      showNotification({
        type: "success",
        message: "Modèle supprimé",
      });
      onClose();
    } catch (error) {
      console.error("Error deleting project template:", error);
    }
  };

  const handleDuplicate = async () => {
    if (!existingTemplate) return;
    try {
      const result = await projectTemplatesApi.create({
        name: `Copie de ${name}`,
        org_id: userData?.org_id || "",
        status: "active",
        themes: structuredClone(themes),
        folders: structuredClone(folders),
      });
      useProjectStore.getState().addProjectTemplate(result);
      showNotification({
        type: "success",
        message: "Modèle dupliqué avec succès",
      });
      onClose();
    } catch (error) {
      console.error("Error duplicating project template:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la duplication du modèle",
      });
    }
  };

  const emailTemplateOptions = [
    { value: "", label: "Aucun" },
    ...emailTemplates.map((et) => ({
      value: et.id,
      label: et.name,
    })),
  ];

  const tabs: { key: TabKey; label: string }[] = [
    { key: "steps", label: "Étapes / Points de contrôle" },
    { key: "folders", label: "Dossiers" },
  ];

  return (
    <>
      <BaseModal
        title={isNew ? "Nouveau modèle de projet" : "Modifier le modèle"}
        startIcon={<Icon1 name="cube" size={20} />}
        onClose={onClose}
        width="max-w-4xl"
      >
        <div className="bg-white flex flex-col gap-4 p-4">
          <TextField
            label="Nom du modèle"
            placeholder="Construction maison individuelle"
            value={name}
            onChange={(value) => setName(value)}
          />

          {/* Tab bar */}
          <div className="flex flex-row border-b">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`px-4 py-2 text-h3 font-medium transition-colors ${
                  activeTab === tab.key
                    ? "text-neutral-900 border-b-2 border-neutral-900"
                    : "text-neutral-400 hover:text-neutral-600"
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Steps tab */}
          {activeTab === "steps" && (
            <div className="flex flex-col gap-1">
              <div className="text-h4 text-neutral-400">
                Thèmes / Étapes / Points de contrôle
              </div>

              <div className="flex flex-col border rounded-xl overflow-hidden" data-themes-container>
                {themes.map((theme, themeIdx) => (
                  <div
                    key={theme.id}
                    data-theme-index={themeIdx}
                    className={`border-b last:border-b-0 transition-all ${
                      draggedThemeIdx === themeIdx ? "opacity-50" : ""
                    } ${dragOverThemeIdx === themeIdx && draggedThemeIdx !== null && draggedThemeIdx !== themeIdx ? "border-t-2 border-t-blue-400" : ""}`}
                  >
                    {/* Theme header */}
                    <div
                      className="flex flex-row items-center gap-2 px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                      onClick={() => toggleTheme(theme.id)}
                    >
                      <div
                        className="cursor-grab active:cursor-grabbing p-0.5 text-neutral-300 hover:text-neutral-500 flex"
                        onPointerDown={(e) => handleThemePointerDown(e, themeIdx)}
                        onPointerMove={handleThemePointerMove}
                        onPointerUp={handleThemePointerUp}
                        onClick={(e) => e.stopPropagation()}
                        style={{ touchAction: "none" }}
                      >
                        <Icon1 name="menu" size={14} color="grey" />
                      </div>
                      <Icon1
                        name={expandedThemes.has(theme.id) ? "down" : "right"}
                        size={12}
                        color="grey"
                      />
                      <input
                        className="flex-1 text-h2 font-medium bg-transparent border-0 outline-none"
                        value={theme.label}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateThemeLabel(theme.id, e.target.value)}
                      />
                      <span className="text-h3 text-neutral-400 mr-2">
                        {theme.steps.length} étape(s)
                      </span>
                      <button
                        className="text-red-400 hover:text-red-600 p-1 flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          setThemeToDelete(theme.id);
                        }}
                      >
                        <Icon1 name="delete" size={20} color="red" />
                      </button>
                    </div>

                    {/* Steps */}
                    {expandedThemes.has(theme.id) && (
                      <div className="pl-6" data-steps-container>
                        {theme.steps.map((step, stepIdx) => (
                          <div
                            key={step.id}
                            data-step-index={stepIdx}
                            className={`border-b last:border-b-0 transition-all ${
                              dragStepCtx?.themeId === theme.id && dragStepCtx?.idx === stepIdx ? "opacity-50" : ""
                            } ${dragOverStepIdx === stepIdx && dragStepCtx !== null && dragStepCtx.themeId === theme.id && dragStepCtx.idx !== stepIdx ? "border-t-2 border-t-blue-400" : ""}`}
                          >
                            {/* Step header */}
                            <div
                              className="flex flex-row items-center gap-2 px-3 py-2 cursor-pointer hover:bg-neutral-50"
                              onClick={() => toggleStep(step.id)}
                            >
                              <div
                                className="cursor-grab active:cursor-grabbing p-0.5 text-neutral-300 hover:text-neutral-500 flex"
                                onPointerDown={(e) => handleStepPointerDown(e, theme.id, stepIdx)}
                                onPointerMove={handleStepPointerMove}
                                onPointerUp={handleStepPointerUp}
                                onClick={(e) => e.stopPropagation()}
                                style={{ touchAction: "none" }}
                              >
                                <Icon1 name="menu" size={14} color="grey" />
                              </div>
                              <Icon1
                                name={expandedSteps.has(step.id) ? "down" : "right"}
                                size={12}
                                color={colors.neutral_400}
                              />
                              <input
                                className="flex-1 text-h2 bg-transparent border-0 outline-none"
                                value={step.label}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  updateStepLabel(theme.id, step.id, e.target.value)
                                }
                              />
                              <span className="text-h3 text-neutral-400 mr-2">
                                {step.controlpoints.length} point(s)
                              </span>
                              <button
                                className="text-red-400 hover:text-red-600 p-1 flex"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeStep(theme.id, step.id);
                                }}
                              >
                                <Icon1 name="delete" size={20} color="red" />
                              </button>
                            </div>

                            {/* Control points */}
                            {expandedSteps.has(step.id) && (
                              <div className="pl-6 pb-2" data-cps-container>
                                {step.controlpoints.map((cp, cpIdx) => (
                                  <div
                                    key={cp.id}
                                    data-cp-index={cpIdx}
                                    className={`flex flex-row items-center gap-2 px-3 py-1 transition-all ${
                                      dragCpCtx?.stepId === step.id && dragCpCtx?.idx === cpIdx ? "opacity-50" : ""
                                    } ${dragOverCpIdx === cpIdx && dragCpCtx !== null && dragCpCtx.stepId === step.id && dragCpCtx.idx !== cpIdx ? "border-t-2 border-t-blue-400" : ""}`}
                                  >
                                    <div
                                      className="cursor-grab active:cursor-grabbing p-0.5 text-neutral-300 hover:text-neutral-500 flex"
                                      onPointerDown={(e) => handleCpPointerDown(e, theme.id, step.id, cpIdx)}
                                      onPointerMove={handleCpPointerMove}
                                      onPointerUp={handleCpPointerUp}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ touchAction: "none" }}
                                    >
                                      <Icon1 name="menu" size={14} color="grey" />
                                    </div>
                                    <input
                                      className="flex-1 text-h3 bg-transparent border-0 outline-none"
                                      value={cp.label}
                                      onChange={(e) =>
                                        updateControlPointLabel(
                                          theme.id,
                                          step.id,
                                          cp.id,
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <div className="w-200">
                                      <DropDown
                                        variant="small"
                                        placeholder="Email..."
                                        options={emailTemplateOptions}
                                        value={
                                          cp.associated_email_template_id || ""
                                        }
                                        onSelect={(val) =>
                                          updateControlPointEmailTemplate(
                                            theme.id,
                                            step.id,
                                            cp.id,
                                            val as string,
                                          )
                                        }
                                        category="Modèle email"
                                      />
                                    </div>
                                    <button
                                      title={
                                        cp.hidden
                                          ? "Non visible client (cliquer pour rendre visible)"
                                          : "Visible client (cliquer pour masquer)"
                                      }
                                      className={`p-1 transition-colors flex ${cp.hidden ? "text-orange-400 hover:text-orange-600" : "text-neutral-300 hover:text-neutral-500"}`}
                                      onClick={() =>
                                        toggleControlPointClientVisibility(
                                          theme.id,
                                          step.id,
                                          cp.id,
                                        )
                                      }
                                    >
                                      <Icon1
                                        name={cp.hidden ? "invisible" : "eye"}
                                        size={20}
                                        color={cp.hidden ? "red" : "grey"}
                                      />
                                    </button>
                                    <button
                                      className="text-red-400 hover:text-red-600 p-1 flex"
                                      onClick={() =>
                                        removeControlPoint(theme.id, step.id, cp.id)
                                      }
                                    >
                                      <Icon1 name="delete" size={20} color="red" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  className="flex flex-row items-center gap-1 px-3 py-1 text-h3 text-neutral-400 hover:text-neutral-600"
                                  onClick={() => addControlPoint(theme.id, step.id)}
                                >
                                  <Icon1 name="plus" size={12} color="grey" />
                                  Point de contrôle
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        <button
                          className="flex flex-row items-center gap-1 px-3 py-2 text-h3 text-neutral-400 hover:text-neutral-600"
                          onClick={() => addStep(theme.id)}
                        >
                          <Icon1 name="plus" size={12} color="grey" />
                          Étape
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <SecondaryButton
                  variant="small"
                  startIcon={<Icon1 name="plus" size={14} />}
                  onClick={addTheme}
                >
                  Thème
                </SecondaryButton>
              </div>
            </div>
          )}

          {/* Folders tab */}
          {activeTab === "folders" && (
            <div className="flex flex-col gap-1">
              <div className="text-h4 text-neutral-400">
                Dossiers et sous-dossiers du projet
              </div>

              <div className="flex flex-col border rounded-xl overflow-hidden">
                {folders.map((folder) => (
                  <div key={folder.id} className="border-b last:border-b-0">
                    {/* Folder header */}
                    <div
                      className="flex flex-row items-center gap-2 px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                      onClick={() => toggleFolder(folder.id)}
                    >
                      <Icon1
                        name={expandedFolders.has(folder.id) ? "down" : "right"}
                        size={12}
                        color="grey"
                      />
                      <Icon1 name="folder" size={16} color={colors.neutral_400} />
                      <input
                        className="flex-1 text-h2 font-medium bg-transparent border-0 outline-none"
                        value={folder.name}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          updateFolderName(folder.id, e.target.value)
                        }
                      />
                      <span className="text-h3 text-neutral-400 mr-2">
                        {(folder.sub_folders || []).length} sous-dossier(s)
                      </span>
                      <button
                        className="p-1 flex"
                        title={
                          folder.notify_conductor_on_upload
                            ? "Le conducteur est notifié quand un document est ajouté"
                            : "Notifier le conducteur lorsqu'un document est ajouté"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFolderNotify(folder.id);
                        }}
                      >
                        <Icon1
                          name="cloche"
                          size={18}
                          color={folder.notify_conductor_on_upload ? "#3b82f6" : "#d4d4d4"}
                        />
                      </button>
                      <button
                        className="text-red-400 hover:text-red-600 p-1 flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFolder(folder.id);
                        }}
                      >
                        <Icon1 name="delete" size={20} color="red" />
                      </button>
                    </div>

                    {/* Sub-folders */}
                    {expandedFolders.has(folder.id) && (
                      <div className="pl-6 pb-2">
                        {(folder.sub_folders || []).map((sf) => (
                          <div
                            key={sf.id}
                            className="flex flex-row items-center gap-2 px-3 py-1"
                          >
                            <Icon1
                              name="folder"
                              size={14}
                              color={colors.neutral_400}
                            />
                            <input
                              className="flex-1 text-h3 bg-transparent border-0 outline-none"
                              value={sf.name}
                              onChange={(e) =>
                                updateSubFolderName(
                                  folder.id,
                                  sf.id,
                                  e.target.value,
                                )
                              }
                            />
                            <button
                              className="p-1 flex"
                              title={
                                sf.notify_conductor_on_upload
                                  ? "Le conducteur est notifié quand un document est ajouté"
                                  : "Notifier le conducteur lorsqu'un document est ajouté"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSubFolderNotify(folder.id, sf.id);
                              }}
                            >
                              <Icon1
                                name="cloche"
                                size={16}
                                color={sf.notify_conductor_on_upload ? "#3b82f6" : "#d4d4d4"}
                              />
                            </button>
                            <button
                              className="text-red-400 hover:text-red-600 p-1 flex"
                              onClick={() =>
                                removeSubFolder(folder.id, sf.id)
                              }
                            >
                              <Icon1 name="delete" size={20} color="red" />
                            </button>
                          </div>
                        ))}
                        <button
                          className="flex flex-row items-center gap-1 px-3 py-1 text-h3 text-neutral-400 hover:text-neutral-600"
                          onClick={() => addSubFolder(folder.id)}
                        >
                          <Icon1 name="plus" size={12} color="grey" />
                          Sous-dossier
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <SecondaryButton
                  variant="small"
                  startIcon={<Icon1 name="plus" size={14} />}
                  onClick={addFolder}
                >
                  Dossier
                </SecondaryButton>
              </div>
            </div>
          )}
        </div>

        <div className="border-t-1 p-4 flex flex-row justify-between">
          <div className="flex flex-row gap-2">
            {!isNew && (
              <SecondaryButton
                variant="small"
                danger={true}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Supprimer
              </SecondaryButton>
            )}
            {!isNew && (
              <SecondaryButton
                variant="small"
                startIcon={<Icon1 name="copy" size={20} />}
                onClick={handleDuplicate}
              >
                Dupliquer
              </SecondaryButton>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <SecondaryButton variant="small" onClick={onClose}>
              Annuler
            </SecondaryButton>
            <Button variant="small" label="Enregistrer" onClick={handleSave} />
          </div>
        </div>
      </BaseModal>

      {showDeleteConfirm && (
        <ConfirmationModal
          title="Supprimer ce modèle de projet ?"
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
        />
      )}

      {themeToDelete && (
        <ConfirmationModal
          title="Supprimer ce thème et toutes ses étapes ?"
          confirmText="CONFIRMER"
          onClose={() => setThemeToDelete(null)}
          onConfirm={() => {
            removeTheme(themeToDelete);
            setThemeToDelete(null);
          }}
        />
      )}
    </>
  );
};
